import { useState, useCallback } from 'react';
import { PhotoStreamClient } from '../api/PhotoStreamClient';
import { PhotoModerationStatus } from '../api/types';

interface UsePhotoUploadOptions {
  pollIntervalMs?: number;
  maxStatusChecks?: number;
}

interface UsePhotoUploadReturn {
  uploading: boolean;
  progress: number;
  error: string | null;
  success: boolean;
  moderationStatus: PhotoModerationStatus | null;
  moderationMessage: string | null;
  isAwaitingModeration: boolean;
  uploadPhoto: (galleryId: string, file: File) => Promise<void>;
  reset: () => void;
}

const DEFAULT_POLL_INTERVAL_MS = 1500;
const DEFAULT_MAX_STATUS_CHECKS = 20;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const usePhotoUpload = (
  apiClient: PhotoStreamClient,
  options?: UsePhotoUploadOptions
): UsePhotoUploadReturn => {
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const maxStatusChecks = options?.maxStatusChecks ?? DEFAULT_MAX_STATUS_CHECKS;

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [moderationStatus, setModerationStatus] = useState<PhotoModerationStatus | null>(null);
  const [moderationMessage, setModerationMessage] = useState<string | null>(null);
  const [isAwaitingModeration, setIsAwaitingModeration] = useState(false);

  const pollStatus = useCallback(
    async (galleryId: string, photoId: string) => {
      for (let attempt = 0; attempt < maxStatusChecks; attempt++) {
        const statusResponse = await apiClient.getPhotoStatus(galleryId, photoId);
        setModerationStatus(statusResponse.status);

        if (statusResponse.status === 'APPROVED') {
          return statusResponse;
        }

        if (statusResponse.status === 'REJECTED' || statusResponse.status === 'ERROR') {
          return statusResponse;
        }

        await delay(pollIntervalMs);
      }

      throw new Error('Moderation timed out');
    },
    [apiClient, maxStatusChecks, pollIntervalMs]
  );

  const uploadPhoto = useCallback(async (galleryId: string, file: File) => {
    try {
      setUploading(true);
      setError(null);
      setSuccess(false);
      setProgress(0);
      setModerationStatus(null);
      setModerationMessage(null);

      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Image file is too large (max 10MB)');
      }

      setProgress(20);

      const uploadResponse = await apiClient.getUploadUrl(galleryId, file.type);
      setProgress(40);

      await apiClient.uploadToS3(uploadResponse.uploadUrl, file);
      setProgress(70);

      setIsAwaitingModeration(true);
      setModerationStatus('PENDING');
      setModerationMessage('Photo uploaded. Awaiting moderation...');
      setProgress(80);

      const statusResult = await pollStatus(galleryId, uploadResponse.photoId);

      setIsAwaitingModeration(false);

      if (statusResult.status === 'REJECTED') {
        setError('Photo was rejected by moderation.');
        setModerationMessage('Photo was rejected. Please choose a different image.');
        return;
      }

      if (statusResult.status === 'ERROR') {
        setError('Photo moderation failed. Please try again.');
        setModerationMessage('Moderation failed. Please try again later.');
        return;
      }

      setProgress(90);
      const finalKey = statusResult.resultKey ?? uploadResponse.s3Key;
      await apiClient.confirmUpload(galleryId, finalKey);
      setProgress(100);
      setSuccess(true);
      setModerationMessage('Photo approved and added to the gallery.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setError(message);
      setModerationStatus('ERROR');
      setModerationMessage(prev => prev ?? message);
    } finally {
      setIsAwaitingModeration(false);
      setUploading(false);
    }
  }, [apiClient, pollStatus]);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
    setSuccess(false);
    setModerationStatus(null);
    setModerationMessage(null);
    setIsAwaitingModeration(false);
  }, []);

  return {
    uploading,
    progress,
    error,
    success,
    moderationStatus,
    moderationMessage,
    isAwaitingModeration,
    uploadPhoto,
    reset
  };
};
